import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Volume2, VolumeX, ChefHat, BellRing } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.18, 0.36].forEach((t, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = [880, 1108, 1318][i];
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.5);
      o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.55);
    });
  } catch { /* audio unsupported */ }
};

export default function TvBoard() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [sound, setSound] = useState(false);
  const [flash, setFlash] = useState(null);
  const known = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/public/tv/${slug}`);
        const ids = new Set(r.data.ready.map((o) => o.id));
        if (known.current) {
          const fresh = r.data.ready.filter((o) => !known.current.has(o.id));
          if (fresh.length) { setFlash(fresh[0].number); setTimeout(() => setFlash(null), 6000); if (sound) playChime(); }
        }
        known.current = ids; setData(r.data);
      } catch { /* retry next tick */ }
    };
    load(); const t = setInterval(load, 4000); return () => clearInterval(t);
  }, [slug, sound]);

  const enableSound = () => { setSound(true); playChime(); };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col" data-testid="tv-board">
      <header className="flex items-center justify-between px-8 py-5 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          {data?.restaurant?.logo_url && <img src={data.restaurant.logo_url} alt="" className="h-12 w-12 rounded-xl object-cover" />}
          <h1 className="font-display text-3xl font-extrabold" data-testid="tv-restaurant-name">{data?.restaurant?.name || "..."}</h1>
        </div>
        <button onClick={sound ? () => setSound(false) : enableSound} data-testid="tv-sound-toggle"
          className={`flex items-center gap-2 rounded-full px-5 py-2 font-bold transition-colors ${sound ? "bg-emerald-500 text-zinc-950" : "bg-amber-400 text-zinc-950 animate-pulse"}`}>
          {sound ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          {sound ? "Som ativado" : "Toque para ativar o som"}
        </button>
      </header>

      {flash && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-emerald-500/95 text-zinc-950" data-testid="tv-flash">
          <div className="text-center">
            <BellRing className="h-24 w-24 mx-auto mb-4 animate-bounce" />
            <div className="text-3xl font-bold uppercase tracking-widest">Pedido pronto</div>
            <div className="font-display font-black text-[14rem] leading-none">#{flash}</div>
          </div>
        </div>
      )}

      <main className="flex-1 grid lg:grid-cols-3 gap-0">
        <section className="lg:col-span-2 p-8">
          <div className="flex items-center gap-3 mb-6 text-emerald-400">
            <BellRing className="h-7 w-7" /><h2 className="text-2xl font-extrabold uppercase tracking-widest">Prontos — retire no balcão</h2>
          </div>
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-5" data-testid="tv-ready-list">
            {(data?.ready || []).map((o) => (
              <div key={o.id} className="rounded-2xl bg-emerald-500 text-zinc-950 p-7 shadow-xl" data-testid={`tv-ready-${o.number}`}>
                <div className="text-xs uppercase font-bold opacity-70">Pedido</div>
                <div className="font-display font-black text-7xl leading-none">#{o.number}</div>
                <div className="mt-3 font-bold text-2xl truncate">{o.customer_name || "Balcão"}</div>
              </div>
            ))}
            {data && data.ready.length === 0 && <div className="col-span-full text-zinc-500 text-xl py-16 text-center">Nenhum pedido pronto no momento.</div>}
          </div>
        </section>
        <aside className="bg-zinc-900 p-8 border-l border-zinc-800">
          <div className="flex items-center gap-3 mb-6 text-amber-400">
            <ChefHat className="h-7 w-7" /><h2 className="text-2xl font-extrabold uppercase tracking-widest">Preparando</h2>
          </div>
          <div className="flex flex-wrap gap-3" data-testid="tv-preparing-list">
            {(data?.preparing || []).map((o) => (
              <div key={o.id} className="rounded-xl bg-zinc-800 px-5 py-3 font-display font-extrabold text-3xl" data-testid={`tv-prep-${o.number}`}>#{o.number}</div>
            ))}
            {data && data.preparing.length === 0 && <div className="text-zinc-500">Nada em preparo.</div>}
          </div>
        </aside>
      </main>
    </div>
  );
}
